import React, { useEffect } from 'react';
import { colors } from 'src/theme';
import {
  i18n,
  useGeneralSoContext,
  openCallMeModal,
  handleErrorMessage,
  showMessage,
} from 'src/common';
import { ContainerComponent, Flex, LoaderSquare, RequestAgentCardV2, StepHeader, Text } from 'src/components';
import { OfferCheckoutCard } from 'src/components/cards/offerCheckout.card';
import { NavigationService } from 'src/common/utils/navigation.util';
import { AppRoutes } from './_router/app.routes';
import { useGetSo } from 'src/common/api/apollo/queries/useGetSo.query';
import { useGetOffersBySo } from 'src/common/api/apollo/queries/useGetOffersBySo.query';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

export const OffersCheckoutPage: React.FC = () => {
  const { soId: soIdFromUrl } = useParams();
  const { setSoId, setOfferId, setSoInitialData, soInitialData } = useGeneralSoContext();
  const { so, loading: soLoading, refetch: refetchSo } = useGetSo();
  const {
    offers,
    loading: offersLoading,
    error: offersError,
    refetch: refetchOffers,
  } = useGetOffersBySo(soIdFromUrl);

  useEffect(() => {
    if (!soIdFromUrl) return;
    setSoId(soIdFromUrl);
    refetchSo({ id: soIdFromUrl });
    refetchOffers({ soId: soIdFromUrl });
  }, [soIdFromUrl, setSoId, refetchSo, refetchOffers]);

  useEffect(() => {
    if (!soInitialData && so?.data && so?.productTypeSpecific?.propertyInfo) {
      setSoInitialData({ data: so.data, propertyInfo: so.productTypeSpecific?.propertyInfo });
    }
  }, [so, soInitialData, setSoInitialData]);

  useEffect(() => {
    if (!offersError) return;
    showMessage({
      type: 'error',
      message: handleErrorMessage(offersError),
      duration: 5000,
      fixed: true,
    });
  }, [offersError]);

  const handleOpenOffer = (offerId: string) => {
    setOfferId(offerId);
    const path = AppRoutes.CHECKOUT.OFFER_CONFIRMATION
      .replace(':soId', encodeURIComponent(soIdFromUrl!))
      .replace(':offerId', encodeURIComponent(offerId));
    NavigationService.navigate(path);
  };

  const showLoading = soLoading || offersLoading;

  return (
    <ContainerComponent>
      <Helmet>
        <title>Ponude | Osiguranje imovine - kompare.hr</title>
      </Helmet>
      <StepHeader />
      <Flex vertical justify="center" gap={16}>
        {showLoading ? (
          <Flex vertical align="center" gap={8}>
            <LoaderSquare />
            <Text type="base" color={colors.text3}>
              {i18n.t('pages.offers.loading')}
            </Text>
          </Flex>
        ) : !offers.length ? (
          <Text type="base" color={colors.text3}>
            {i18n.t('pages.offers.noOffers')}
          </Text>
        ) : (
          <Flex vertical gap={24}>
            {offers.map((offer) => (
              <OfferCheckoutCard
                key={offer._id}
                offer={offer}
                onSelectOffer={() => handleOpenOffer(offer._id)}
              />
            ))}
          </Flex>
        )}
        <RequestAgentCardV2 onButtonClick={openCallMeModal} />
      </Flex>
    </ContainerComponent>
  );
};

export default OffersCheckoutPage;
